import { ReactNode } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileGameOptions } from "@/components/MobileGameOptions";
import { UserMenu } from "@/components/UserMenu";
import { Button } from "@/components/ui/button";
import { CurrencyDisplay } from "@/components/ui/CurrencyDisplay";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { LogIn } from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { Footer } from "@/components/Footer";
import { FloatingDecorations } from "@/components/FloatingDecorations";
import { ThemeToggle } from "@/components/ThemeToggle";

interface ChessAppLayoutProps {
  children: ReactNode;
  showGameOptions?: boolean;
  showFooter?: boolean;
  onStartGame?: (mode: 'classic' | 'hatch', opponent: string, isBot: boolean, elo?: number) => void;
  onStartEngine?: () => void;
  onStartMultiplayer?: () => void;
  onStartMatchmaking?: () => void;
  onShowBotChallenge?: () => void;
}

export function ChessAppLayout({ 
  children, 
  showGameOptions = true,
  showFooter = true,
  onStartGame,
  onStartEngine,
  onStartMultiplayer,
  onStartMatchmaking,
  onShowBotChallenge
}: ChessAppLayoutProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-to-b from-teal-50/80 via-emerald-50/50 to-cyan-50/60 dark:from-slate-900 dark:via-purple-950/95 dark:to-slate-950 mobile-safe-container">
        <AppSidebar 
          onStartGame={onStartGame}
          onStartEngine={onStartEngine}
          onStartMultiplayer={onStartMultiplayer}
          onStartMatchmaking={onStartMatchmaking}
          onShowBotChallenge={onShowBotChallenge}
        />
        
        <div className="flex-1 flex flex-col min-h-screen max-w-full overflow-x-hidden">
          {/* Vibrant Header */}
          <header className="h-14 flex items-center justify-between border-b border-border px-4 bg-gradient-to-r from-teal-50/90 via-white to-emerald-50/90 backdrop-blur-lg dark:from-slate-900 dark:via-purple-900/95 dark:to-slate-900 shadow-lg">
            <div className="flex items-center gap-3 group">
              <SidebarTrigger className="text-foreground/80 hover:text-foreground hover:bg-muted rounded-md p-2 transition-colors" />
              <Logo variant="full" size="md" loading="eager" />
            </div>
            
            <div className="flex items-center gap-3">
              <ThemeToggle />
              {user ? (
                <>
                  <CurrencyDisplay className="hidden sm:flex" />
                  <UserMenu />
                </>
              ) : (
                <Button onClick={() => navigate('/auth')} variant="glass" size="sm" className="text-foreground border-border hover:bg-muted">
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </Button>
              )}
            </div>
          </header>

          {/* Main Content Area - Dark gradient with floating decorations */}
          <main className="flex-1 overflow-y-auto bg-gradient-to-b from-teal-50/60 via-emerald-50/40 to-cyan-50/50 dark:from-slate-900 dark:via-purple-950/95 dark:to-slate-950 relative">
            <FloatingDecorations density="low" className="z-0" />
            <div className="h-full relative z-10">
              {children}
            </div>
          </main>

          {/* Footer */}
          {showFooter && <Footer />}

          {/* Mobile Floating Action Button */}
          {showGameOptions && isMobile && onStartGame && onStartEngine && onStartMultiplayer && onStartMatchmaking && (
            <MobileGameOptions
              onStartGame={onStartGame}
              onStartEngine={onStartEngine}
              onStartMultiplayer={onStartMultiplayer}
              onStartMatchmaking={onStartMatchmaking}
              onShowBotChallenge={onShowBotChallenge}
            />
          )}
        </div>
      </div>
    </SidebarProvider>
  );
}